import { ArchitectureParseError } from "./errors";
import { mapLegacyToCanonical } from "./legacy-mapper";
import { parseContainers } from "./parse-containers";
import { parseDecisions } from "./parse-decisions";
import { parseDataFlows } from "./parse-flows";
import { parseFrontmatter } from "./parse-frontmatter";
import { parseOperationalNotes } from "./parse-operational-notes";
import { splitH2Sections } from "./parse-sections";
import { foldSoftWrappedQuotes } from "./parse-soft-wrap";
import { parseSystemContext } from "./parse-system-context";
import { stripDocumentFence } from "./strip-document-fence";
import type { ArchitectureData, Meta } from "./types";
import { parseArchitectureV3 } from "./v3/parse-architecture-v3";
import { parseArchitectureV4 } from "./v4/parse-architecture-v4";

export { ArchitectureParseError };

type Handler = "system" | "containers" | "flows" | "decisions" | "ops";

const SECTION_HANDLERS: Record<string, Handler> = {
  "system context": "system",
  containers: "containers",
  "data flows": "flows",
  decisions: "decisions",
  "operational notes": "ops",
};

const RECOGNIZED_H2S = [
  "## System Context",
  "## Containers",
  "## Data Flows",
  "## Decisions",
  "## Operational Notes",
];

const TEMPLATE_HINT = "See docs/architecture-template.md for the canonical format.";

function isEmptyBody(body: string): boolean {
  return body.trim().length === 0;
}

function chooseDriftHint(body: string, sectionsFound: number): string {
  const lines = body.split("\n");
  // Indented H2: at least one line that would have been an H2 if it weren't
  // indented. This is the failure mode when a model emits the document with
  // a uniform leading indent — `splitH2Sections` now strips the common
  // prefix, so we only reach this when indentation is mixed.
  const hasIndentedH2 = lines.some((l) => /^[ \t]+##\s+\S/.test(l));
  // H1 drift: a `# Title` line with no surrounding `##` heading at column 0.
  const hasH1 = lines.some((l) => /^#\s+\S/.test(l));

  if (sectionsFound === 0) {
    if (hasIndentedH2) {
      return "Hint: H2 headings were found but they have leading whitespace. Every line must start at column 0 — re-emit the document with no leading indent.";
    }
    if (hasH1) {
      return "Hint: the exporter may have emitted H1 (`# Section`) instead of H2 (`## Section`).";
    }
    return "Hint: no H2 headings were found. The exporter may have wrapped the document in a code fence or stripped the headings entirely.";
  }
  return "Hint: section titles must match exactly (case-insensitive). Common drift: H1 instead of H2, or extra words like '(C4 L1)' in the title.";
}

// archviz/v0.3 is the level-first schema introduced with the workshop
// redesign. The dispatch matches case-insensitively against the
// frontmatter `schema:` string. Anything that doesn't match falls through
// to the legacy v0.1/v0.2 parser; missing or garbage `schema:` values
// (we've seen `archviz/0.1`, `SolutionViz/v0.2`, and outright absent in
// the wild) are routed to legacy so existing docs keep rendering.
const V03_PATTERN = /^archviz\/v?0\.3(?:[.\-+].*)?$/i;

// archviz/v0.4 adds optional `permissions:` and `endpoints:` blocks under
// external systems for L3-of-external API surface detail. Additive on
// read: v0.4 docs that omit the new blocks parse identically to v0.3.
const V04_PATTERN = /^archviz\/v?0\.4(?:[.\-+].*)?$/i;

function isV03(meta: Meta): boolean {
  const s = (meta.schema ?? "").trim();
  return V03_PATTERN.test(s);
}

function isV04(meta: Meta): boolean {
  const s = (meta.schema ?? "").trim();
  return V04_PATTERN.test(s);
}

// Decoupled from parser dispatch: answers "could this document have carried
// permissions/endpoints on its externals?" — true for archviz/v0.4 and any
// later version, false for v0.3 and earlier, missing schemas, and anything
// unparseable. Used by the UI to choose between "no API surface documented"
// and "loaded under an older schema — re-upload to enable" hover hints on
// non-drillable external systems.
const SCHEMA_VERSION_PATTERN = /^archviz\/v?(\d+)\.(\d+)(?:[.\-+].*)?$/i;

export function schemaSupportsApiSurface(meta: Meta): boolean {
  const s = (meta.schema ?? "").trim();
  const match = s.match(SCHEMA_VERSION_PATTERN);
  if (!match) return false;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  if (Number.isNaN(major) || Number.isNaN(minor)) return false;
  if (major > 0) return true;
  return minor >= 4;
}

export function parseArchitecture(markdown: string): ArchitectureData {
  rejectRtf(markdown);
  // Strip a document-wide wrapping code fence if the model emitted one
  // despite the prompt's "no code fences" instruction. Only matches a
  // fence that wraps the entire document end-to-end; inline fences
  // (indented under YAML quoted strings) are left alone.
  const unfenced = stripDocumentFence(markdown);
  // Fold multi-line quoted strings on the whole document up front so every
  // downstream parser (v0.2 and v0.3, both paths) sees joined values
  // instead of truncated first lines. The fold only fires inside an open
  // quote, so headings and structural bullets are untouched.
  const folded = foldSoftWrappedQuotes(unfenced);
  const { meta, body } = parseFrontmatter(folded);
  if (isV04(meta)) {
    return parseV04(meta, body);
  }
  if (isV03(meta)) {
    return parseV03(meta, body);
  }
  return mapLegacyToCanonical(parseV02(meta, body));
}

function rejectRtf(markdown: string): void {
  // Real failure mode: a user "Save As" in TextEdit on macOS writes RTF
  // even when the file is named `.md`. The existing parser fails with a
  // generic "no recognized sections" hint that points users at the wrong
  // problem; reject up front with a specific message.
  const stripped = markdown.replace(/^[\s﻿]+/, "");
  if (stripped.startsWith("{\\rtf")) {
    throw new ArchitectureParseError(
      [
        "This file appears to be RTF, not markdown. Re-save as plain text",
        "(.md) — in TextEdit, use Format → Make Plain Text before saving,",
        "or use a code-friendly editor.",
      ].join(" "),
    );
  }
}

function parseV03(meta: Meta, body: string): ArchitectureData {
  return parseArchitectureV3(meta, body);
}

function parseV04(meta: Meta, body: string): ArchitectureData {
  return parseArchitectureV4(meta, body);
}

function parseV02(meta: Meta, body: string): ArchitectureData {
  const result: ArchitectureData = {
    meta,
    actors: [],
    externalSystems: [],
    containers: [],
    dataFlows: [],
    decisions: [],
    operationalNotes: [],
  };

  const sections = splitH2Sections(body);
  const recognized = sections.filter((s) => SECTION_HANDLERS[s.title.toLowerCase()] !== undefined);

  if (recognized.length === 0) {
    const foundTitles = sections.map((s) => `## ${s.title}`);
    const foundLine =
      foundTitles.length === 0
        ? "  (no H2 headings were found at all)"
        : foundTitles.map((t) => `  - ${t}`).join("\n");
    const hint = chooseDriftHint(body, sections.length);
    throw new ArchitectureParseError(
      [
        "Architecture document has no recognized sections.",
        "",
        "Expected at least one of:",
        ...RECOGNIZED_H2S.map((t) => `  - ${t}`),
        "",
        "Found:",
        foundLine,
        "",
        hint,
        TEMPLATE_HINT,
      ].join("\n"),
    );
  }

  for (const section of sections) {
    const key = section.title.toLowerCase();
    const handler = SECTION_HANDLERS[key];
    if (!handler) {
      console.warn(`[arch-parser] skipping unknown section: ${section.title}`);
      continue;
    }
    try {
      switch (handler) {
        case "system": {
          const { actors, externalSystems } = parseSystemContext(section.body);
          result.actors = actors;
          result.externalSystems = externalSystems;
          break;
        }
        case "containers":
          result.containers = parseContainers(section.body);
          break;
        case "flows":
          result.dataFlows = parseDataFlows(section.body);
          break;
        case "decisions":
          result.decisions = parseDecisions(section.body);
          break;
        case "ops":
          result.operationalNotes = parseOperationalNotes(section.body);
          break;
      }
    } catch (err) {
      if (err instanceof ArchitectureParseError) throw err;
      console.warn(`[arch-parser] section "${section.title}" failed:`, err);
    }
  }

  for (const section of recognized) {
    const handler = SECTION_HANDLERS[section.title.toLowerCase()];
    if (!handler) continue;
    if (isEmptyBody(section.body)) continue;
    const isEmpty = sectionIsEmpty(handler, result);
    if (isEmpty) {
      throw new ArchitectureParseError(
        [
          `Section \`## ${section.title}\` has content but no entries could be parsed from it.`,
          "",
          describeExpectedShape(handler),
          "",
          TEMPLATE_HINT,
        ].join("\n"),
      );
    }
  }

  return result;
}

function sectionIsEmpty(handler: Handler, result: ArchitectureData): boolean {
  switch (handler) {
    case "system":
      return result.actors.length === 0 && result.externalSystems.length === 0;
    case "containers":
      return result.containers.length === 0;
    case "flows":
      return result.dataFlows.length === 0;
    case "decisions":
      return result.decisions.length === 0;
    case "ops":
      return result.operationalNotes.length === 0;
  }
}

function describeExpectedShape(handler: Handler): string {
  switch (handler) {
    case "system":
      return [
        "Expected `### Actors` and/or `### External Systems` subsections, each with bullet entries like:",
        "  - **actor-slug**",
        '    - label: "Display Name"',
        "    - type: person",
      ].join("\n");
    case "containers":
      return [
        "Expected one or more H3 container entries like:",
        "  ### container-slug",
        '  - label: "Display Name"',
        "  - type: backend",
      ].join("\n");
    case "flows":
      return [
        "Expected one or more H3 flow entries with a `#### Steps` block:",
        "  ### flow-slug",
        '  - label: "Flow label"',
        "  #### Steps",
        '  1. **source** → **target**: "description"',
      ].join("\n");
    case "decisions":
      return [
        "Expected one or more H3 decision entries:",
        "  ### decision-slug",
        '  - label: "Decision label"',
        "  - status: accepted",
      ].join("\n");
    case "ops":
      return [
        "Expected one or more H3 operational-note entries:",
        "  ### note-slug",
        '  - label: "Note label"',
        "  - type: cost",
        "  - severity: warning",
      ].join("\n");
  }
}
